// src/oura/onboarding/recordCheck.ts
//
// The "Business record" card's rows and sentence (#4153), from the backend's
// `businessRecordCheck` (ourteam-backend#5808). Pure, so the sentence can be
// checked without drawing the card.
//
// The backend compares and normalises; nothing here decides whether two values
// agree. A row is drawn from the `state` it was sent with.

import type { BusinessRecordCheck, OutletName, RecordCheckField, RecordCheckState } from "@service/aeo/clientReview";

export interface RecordRow {
    key: string;
    label: string;
    field: RecordCheckField;
    /** The outlet's slug for an outlet row, null for the business's own name and website. */
    outlet: string | null;
}

/** Name, Website, then Address and Phone for each outlet, in the order the backend sent them. */
export function recordRows(check: BusinessRecordCheck, outletNames: readonly OutletName[] | null | undefined): RecordRow[] {
    // The name the check itself carries, then the engagement's, then the slug: a
    // row never reads as a bare slug while either name is on hand.
    const nameOf = (o: BusinessRecordCheck["outlets"][number]) =>
        o.name?.trim() || outletNames?.find((n) => n.slug === o.slug)?.name?.trim() || o.slug || "Outlet";
    const several = check.outlets.length > 1;
    const rows: RecordRow[] = [
        { key: "name", label: "Name", field: check.business.name, outlet: null },
        { key: "website", label: "Website", field: check.business.website, outlet: null },
    ];
    for (const o of check.outlets) {
        const who = several ? `${nameOf(o)} ` : "";
        rows.push({ key: `${o.slug}:address`, label: `${who}address`.replace(/^./, (c) => c.toUpperCase()), field: o.address, outlet: o.slug });
        rows.push({ key: `${o.slug}:phone`, label: `${who}phone`.replace(/^./, (c) => c.toUpperCase()), field: o.phone, outlet: o.slug });
    }
    return rows;
}

export const RECORD_STATE_WORD: Record<RecordCheckState, string> = {
    match: "Match",
    mismatch: "Mismatch",
    missing: "Not recorded",
};

/** "2 of 8 fields match, 1 differ, 5 not recorded": zero parts are left out. */
export function recordSummary(rows: readonly RecordRow[]): string {
    if (rows.length === 0) return "Nothing to compare";
    const count = (s: RecordCheckState) => rows.filter((r) => r.field.state === s).length;
    const parts = [`${count("match")} of ${rows.length} fields match`];
    if (count("mismatch") > 0) parts.push(`${count("mismatch")} differ`);
    if (count("missing") > 0) parts.push(`${count("missing")} not recorded`);
    return parts.join(", ");
}

/**
 * The folds a Reviews hash opens: the Google profile card, and the outlet's own
 * block when the hash names one. Empty for any other hash. The ids are the ones
 * `GbpProfileCard` draws (`gbp-profile`, `gbp-<slug>`, `gbp-one` for the one
 * outlet); the inverse of `googleProfileHash`.
 */
export function googleProfileFolds(hash: string): string[] {
    if (hash === "#gbp-profile") return ["gbp-profile"];
    if (hash.startsWith("#gbp-") && hash.length > "#gbp-".length) return ["gbp-profile", hash.slice(1)];
    return [];
}

/** The Reviews hash that opens the Google profile card, on one outlet when given its slug. */
export function googleProfileHash(outlet: string | null): string {
    return outlet === null ? "#gbp-profile" : `#gbp-${outlet || "one"}`;
}
